import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Loader2, Package } from "lucide-react";
import { Logo } from "../components/Logo";
import { useLang } from "../i18n";
import { productByKey } from "../data/products";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-oris-green/20 bg-oris-cream px-4 py-3.5 text-sm text-oris-ink placeholder:text-oris-moss/50 transition-[border-color] duration-300 focus:border-oris-gold";

export default function ProductPage() {
  const { key } = useParams();
  const { t, lang } = useLang();
  const product = productByKey(key);
  const [form, setForm] = useState({ name: "", company: "", email: "", quantity: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [key]);

  if (!product) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-oris-cream px-5 text-center">
        <p data-testid="product-not-found" className="font-serif text-3xl text-oris-ink">{t.product.notFound}</p>
        <Link to="/" className="bg-oris-green px-6 py-3 text-xs font-bold uppercase tracking-widest text-oris-cream hover:bg-oris-deep">
          {t.product.back}
        </Link>
      </div>
    );
  }

  const d = product[lang];
  const p = t.product;
  const f = t.contact.form;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const message = form.quantity
        ? `${p.quantity} : ${form.quantity}\n\n${form.message}`
        : form.message;
      await axios.post(`${API}/contact`, {
        name: form.name,
        company: form.company,
        email: form.email,
        subject: `Cotation — ${d.name}`,
        message,
      });
      toast.success(f.success);
      setForm({ name: "", company: "", email: "", quantity: "", message: "" });
    } catch (err) {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="product-page" className="min-h-screen bg-oris-cream">
      <div className="border-b border-oris-green/10 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
          <Link to="/" data-testid="product-logo-link" aria-label="ORIS INTERNATIONAL">
            <Logo tone="dark" compact />
          </Link>
          <Link
            to="/#produits"
            data-testid="product-back-link"
            className="inline-flex items-center gap-2 border border-oris-green/25 px-4 py-2 text-xs font-bold uppercase tracking-widest text-oris-green transition-colors hover:border-oris-gold hover:text-oris-gold"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {p.back}
          </Link>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-5 py-14 sm:px-8 lg:py-20">
        <div className="grid gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="spotlight-frame group overflow-hidden border border-oris-green/12">
              <img
                src={product.img}
                alt={d.name}
                data-testid="product-image"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>
          </Reveal>

          <div>
            <Reveal>
              <span className="mb-5 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.24em] text-oris-moss">
                <span className="gold-rule" aria-hidden="true" />
                {product.category === "raw" ? p.categoryRaw : p.categoryFrozen}
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 data-testid="product-title" className="font-serif text-5xl font-medium tracking-tight text-oris-ink lg:text-6xl">
                {d.name}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-oris-moss lg:text-lg">{d.desc}</p>
            </Reveal>

            <Reveal delay={0.15}>
              <h2 className="mt-12 mb-4 text-xs font-bold uppercase tracking-[0.24em] text-oris-green">{p.specsTitle}</h2>
              <dl data-testid="product-specs" className="divide-y divide-oris-green/10 border-y border-oris-green/10">
                {d.specs.map(([label, value]) => (
                  <div key={label} className="grid grid-cols-12 gap-4 py-3.5">
                    <dt className="col-span-4 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-oris-moss sm:col-span-3">{label}</dt>
                    <dd className="col-span-8 font-serif text-lg text-oris-ink sm:col-span-9">{value}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>

            <Reveal delay={0.2}>
              <h2 className="mt-10 mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-oris-green">
                <Package className="h-4 w-4" /> {p.packingTitle}
              </h2>
              <div data-testid="product-packing" className="flex flex-wrap gap-3">
                {d.packing.map(([label, value]) => (
                  <div key={label} className="border border-oris-green/15 bg-white px-4 py-3">
                    <div className="text-[0.62rem] font-bold uppercase tracking-[0.18em] text-oris-gold">{label}</div>
                    <div className="mt-1 text-sm text-oris-ink">{value}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div data-testid="quote-form-card" className="mt-20 border border-oris-green/12 bg-white shadow-[0_18px_60px_rgb(15,41,24,0.07)]">
            <div className="grain relative bg-oris-green px-7 py-8 sm:px-10">
              <h2 className="relative font-serif text-3xl font-medium text-oris-cream lg:text-4xl">{p.quoteTitle}</h2>
              <p className="relative mt-2 max-w-xl text-sm text-oris-cream/75">{p.quoteIntro}</p>
            </div>
            <form data-testid="quote-form" onSubmit={submit} className="grid gap-6 p-7 sm:grid-cols-2 sm:p-10">
              <div>
                <label htmlFor="quote-name" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">{f.name} *</label>
                <input id="quote-name" data-testid="quote-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="quote-company" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">{f.company}</label>
                <input id="quote-company" data-testid="quote-company-input" value={form.company} onChange={set("company")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="quote-email" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">{f.email} *</label>
                <input id="quote-email" data-testid="quote-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="quote-quantity" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">{p.quantity}</label>
                <input id="quote-quantity" data-testid="quote-quantity-input" placeholder={p.quantityPh} value={form.quantity} onChange={set("quantity")} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="quote-message" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">{f.message} *</label>
                <textarea id="quote-message" data-testid="quote-message-input" required rows={5} placeholder={p.messagePh} value={form.message} onChange={set("message")} className={`${inputCls} resize-y`} />
              </div>
              <div className="sm:col-span-2">
                <button
                  data-testid="quote-submit-button"
                  type="submit"
                  disabled={sending}
                  className="group inline-flex items-center gap-3 bg-oris-green px-8 py-4 text-[0.8rem] font-bold uppercase tracking-[0.16em] text-oris-cream transition-colors duration-300 hover:bg-oris-deep disabled:opacity-60"
                >
                  {sending ? f.sending : p.submit}
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  )}
                </button>
              </div>
            </form>
          </div>
        </Reveal>
      </main>
    </div>
  );
}
